import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider } from 'core/states';
import { goTo } from 'core/utils';
import { disciplineService } from 'corporate/services';
import { useJobActions } from 'jobs/hooks';
import { oldBomPartListPath } from 'jobs/routes/routePaths';
import { IJobForm } from 'jobs/types';
import { ProjectFormField } from 'projects/components';

import { mapUpsertJobForm } from './mappers';
import { addJobSchema } from './schema';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
  projectId?: number;
}

export const AddJob = ({ isOpen, onClose, onSubmitSuccess, projectId }: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { upsertJob } = useJobActions();

  const displayDisciplineField = useMemo(
    () => Boolean(user?.companySettings?.bomDisciplinesEnabled),
    [user?.companySettings?.bomDisciplinesEnabled]
  );

  const onSubmit = (reset: UseFormReset<IJobForm>) => async (data: IJobForm) => {
    const { success, bom: job } = await upsertJob(mapUpsertJobForm(data, projectId));

    if (!success) {
      return;
    }

    setTimeout(() => {
      handleOnClose(reset);
      onSubmitSuccess?.();

      goTo(oldBomPartListPath(job.projectId, job.id));
    }, 500);
  };

  const handleOnClose = (reset: UseFormReset<IJobForm>) => {
    reset();
    onClose();
  };

  return (
    <FormProvider schema={addJobSchema(!projectId)}>
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        title={t('titleCreateJob')}
        submitBtnLabel={t('buttonLabelCreate')}
        onSubmit={onSubmit}
      >
        {!projectId && <ProjectFormField />}

        <FormField
          name="jobName"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelJobName'),
            required: true,
          }}
        />

        {displayDisciplineField && (
          <FormField
            name="discipline"
            type={InputType.AUTOCOMPLETE}
            inputProps={{
              label: t('labelDiscipline'),
              placeholder: t('labelDiscipline'),
              getOptions: disciplineService.getAllDisciplines,
              enabledOnFocus: false,
            }}
          />
        )}
      </Form>
    </FormProvider>
  );
};
